/**
 * The extracted labs as a compiled results list on the first onboarding step: each mapped marker
 * with its printed value, unit, range and flag, so the visitor sees the numbers that will drive
 * the mix, not a row of names (ONB-021). Missing wanted markers stay in amber. Submitting with
 * gaps is allowed.
 */
import * as stylex from "@stylexjs/stylex";
import { biomarker, type MarkerResult, type ProcessingProgress } from "@lattice/core";
import "@lattice/tokens/color.stylex";
import { color } from "@lattice/tokens/color.stylex";
import "@lattice/tokens/space.stylex";
import { space } from "@lattice/tokens/space.stylex";
import "@lattice/tokens/text.stylex";
import { text } from "@lattice/tokens/text.stylex";
import { stepText } from "./Shell.tsx";
const VALUE = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 3
});
const styles = {
  flagged: {
    kMwMTN: "x1j243yh",
    $$css: true
  },
  abnormal: {
    kMwMTN: "x156x1qg",
    $$css: true
  },
  range: {
    kGuDYH: "xl4dl4e",
    kMwMTN: "x1jty25b",
    kcqcaj: "x1l1vl4p",
    $$css: true
  },
  flag: {
    kGuDYH: "xl4dl4e",
    k9WMMc: "xp4054r",
    k63SB2: "xqu410z",
    kLWn49: "x1e5flnj",
    $$css: true
  }
};
function rangeOf(result: MarkerResult): string | null {
  if (result.referenceText !== null) return result.referenceText;
  if (result.referenceLow !== null && result.referenceHigh !== null) return `${VALUE.format(result.referenceLow)} - ${VALUE.format(result.referenceHigh)}`;
  return null;
}
function valueOf(result: MarkerResult): string {
  if (result.valueKind === "text" || result.valueNumeric === null) {
    const printed = result.valueText ?? "Present";
    return result.unit === null ? printed : `${printed} ${result.unit}`;
  }
  const amount = VALUE.format(result.valueNumeric);
  return result.unit === null ? amount : `${amount} ${result.unit}`;
}
type FlagView = {
  readonly label: string;
  readonly tone: "warn" | "danger" | "dim";
};
function flagOf(flag: MarkerResult["flag"]): FlagView | null {
  switch (flag) {
    case "low":
      return {
        label: "Low",
        tone: "warn"
      };
    case "high":
      return {
        label: "High",
        tone: "warn"
      };
    case "abnormal":
      return {
        label: "Abnormal",
        tone: "danger"
      };
    case "normal":
      return {
        label: "In range",
        tone: "dim"
      };
    case null:
      return null;
  }
}
function flagTone(tone: FlagView["tone"]) {
  if (tone === "warn") return styles.flagged;
  if (tone === "danger") return styles.abnormal;
  return styles.range;
}
function ResultRow({
  result
}: {
  readonly result: MarkerResult;
}) {
  const flag = flagOf(result.flag);
  const range = rangeOf(result);
  const outside = result.flag !== null && result.flag !== "normal";
  return <li className="xrvj5dj xju1xpo x1vdzsm xcfm6fe xp1t01x x1t74ka8 x1q0q8m5 xso031l" data-testid="finding-row">
      <span className="xqu410z x1e5flnj xeuugli">{biomarker(result.code).name}</span>
      <span {...{
      0: {
        className: "x1l1vl4p xqu410z x1e5flnj xp4054r"
      },
      1: {
        className: "x1l1vl4p xqu410z x1e5flnj xp4054r x1j243yh"
      }
    }[!!outside << 0]}>{valueOf(result)}</span>
      {(range !== null || flag !== null) && <span className="xl4dl4e x1jty25b x1l1vl4p">{range ?? ""}</span>}
      {flag !== null && <span {...stylex.props(styles.flag, flagTone(flag.tone))}>{flag.label}</span>}
    </li>;
}
export function Findings({
  progress
}: {
  readonly progress: ProcessingProgress;
}) {
  const found = progress.results;
  const missing = progress.markersMissing;
  return <section className="x78zum5 xdt5ytf x1r0x45p" data-testid="findings" aria-labelledby="findings-heading">
      <div {...stylex.props(stepText.block)}>
        <h2 id="findings-heading" {...stylex.props(stepText.subheading)}>
          Your results
        </h2>
        <p {...stylex.props(stepText.lead)}>
          These values are from your labs. Add another file if something is missing.
        </p>
      </div>
      {found.length === 0 ? <p className="x1ghz6dp xl4dl4e x1jty25b x1fzhlzt">We found no markers in this report.</p> : <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" aria-label="Results in your labs">
          {found.map(result => <ResultRow key={result.code} result={result} />)}
        </ul>}
      {missing.length > 0 && <>
          <p className="x1ghz6dp xl4dl4e x1jty25b x1fzhlzt">Not in this report</p>
          <ul className="xe8uvvx x1ghz6dp x1717udv x78zum5 xdt5ytf" aria-label="Markers not in this report">
            {missing.map(code => <li key={code} className="xrvj5dj xju1xpo x1vdzsm xcfm6fe xp1t01x x1t74ka8 x1q0q8m5 xso031l">
                <span className="x1j243yh xl4dl4e">{biomarker(code).name}</span>
              </li>)}
          </ul>
        </>}
    </section>;
}